import React, { useState, useEffect } from"react";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import axios from"axios";
import { PlusIcon, TrashIcon } from"@heroicons/react/24/outline";
import API_BASE_URL from"../Config/config";

const MultiNotes = () => {
 const [notes, setNotes] = useState([]);
 const [activeNoteId, setActiveNoteId] = useState(null);
 const [userId, setUserId] = useState(null);
 const navigate = useNavigate();

 useEffect(() => {
 const user = JSON.parse(localStorage.getItem("user"));
 if (user?.userId) {
 setUserId(user.userId);
 fetchNotes(user.userId);
 }
 }, []);

 const fetchNotes = async (uid) => {
 try {
 const res = await axios.get(`${API_BASE_URL}/notes/${uid}`);
 const fetchedNotes = res.data.map((note, i) => ({
 ...note,
 id: note.id || Date.now() + i,
 }));
 setNotes(fetchedNotes);
 if (fetchedNotes.length) setActiveNoteId(fetchedNotes[0].id);
 } catch (err) {
 console.error("Failed to fetch notes", err);
 }
 };

 const syncNotesToBackend = async (updatedNotes) => {
 try {
 await axios.post(`${API_BASE_URL}/notes/${userId}`, {
 notes: updatedNotes,
 });
 } catch (err) {
 console.error("❌ Failed to save notes", err);
 }
 };

 const createNote = async () => {
 const newNote = {
 id: Date.now(),
 title: `Untitled ${notes.length + 1}`,
 content:"",
 };
 const updated = [newNote, ...notes];
 setNotes(updated);
 setActiveNoteId(newNote.id);
 await syncNotesToBackend(updated);
 };

 const updateNote = async (key, value) => {
 const updatedNotes = notes.map((note) =>
 note.id === activeNoteId ? { ...note, [key]: value } : note
 );
 setNotes(updatedNotes);
 await syncNotesToBackend(updatedNotes);
 };

  const deleteNote = async (id) => {
    const updated = notes.filter((n) => n.id !== id);
    setNotes(updated);
    setActiveNoteId(updated.length ? updated[0].id : null);
    
    // 🔥 Ensure we sync the CURRENT updated state, not potentially stale closure values
    try {
      await axios.post(`${API_BASE_URL}/notes/${userId}`, {
        notes: updated,
      });
      console.log("✅ Deletion synced to backend");
    } catch (err) {
      console.error("❌ Failed to save notes after deletion", err);
    }
  };

 const activeNote = notes.find((n) => n.id === activeNoteId);

 return (
 <div className="min-h-screen bg-bgMain flex items-center justify-center px-4 py-10 relative overflow-hidden">
 {/* Background decoration */}
 <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -mr-48 -mt-48"></div>
 
 <div
 className={`w-full max-w-5xl rounded-3xl p-8 transition-all duration-500 relative z-10 border-2 border-[#5B21B6] shadow-2xl ${
 notes.length > 0
 ?"bg-[#F9F7FF]"
 :"bg-[#F9F7FF]/50"
 }`}
 style={{ boxShadow:"0 0 40px rgba(91, 33, 182, 0.15)" }}
 >
 <div className="flex justify-between items-center mb-8 pb-6 border-b border-accentGray/30">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors group"
              title="Go Back"
            >
              <ChevronLeftIcon className="w-6 h-6 text-primary group-hover:-translate-x-1 transition-transform" />
            </button>
            <h1 className="text-3xl font-extrabold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent flex items-center gap-3 uppercase tracking-tighter">
              📝 My Study Notes
            </h1>
          </div>
          <button
            onClick={createNote}
            className="btn-primary-gradient px-6 py-2.5 rounded-xl text-sm flex items-center gap-2 shadow-lg hover:scale-105 active:scale-95 transition-all"
          >
            <PlusIcon className="h-5 w-5" /> New Note
          </button>
        </div>

 {notes.length === 0 ? (
 <div className="text-center py-20">
 <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
 <PlusIcon className="h-10 w-10 text-primary" />
 </div>
 <p className="text-muted text-xl font-medium">
 No notes yet. Start your journey by creating your first note!
 </p>
 </div>
 ) : (
 <>
 <div className="flex mb-8 overflow-x-auto space-x-3 pb-2 custom-scrollbar">
 {notes.map((note) => (
 <button
 key={note.id}
 className={`px-6 py-3 rounded-2xl border text-sm font-bold whitespace-nowrap transition-all duration-300 ${
 activeNoteId === note.id
 ?"bg-primary text-white border-primary shadow-lg shadow-primary/20"
 :"bg-bgMain border-accentGray/50 text-muted hover:border-primary/50 hover:text-body"
 }`}
 onClick={() => setActiveNoteId(note.id)}
 >
 {note.title}
 </button>
 ))}
 </div>

 {activeNote && (
 <div className="mt-6 bg-[#F9F7FF] p-8 rounded-3xl border-2 border-[#5B21B6] animate-fadeIn">
 <input
 type="text"
 value={activeNote.title}
 onChange={(e) => updateNote("title", e.target.value)}
 className="w-full text-2xl font-bold bg-transparent border-b-2 border-primary/30 mb-8 pb-4 focus:border-primary focus:outline-none text-heading transition-colors"
 placeholder="Enter Note Title..."
 />

 <textarea
 value={activeNote.content}
 onChange={(e) => updateNote("content", e.target.value)}
 rows={12}
 className="w-full p-6 bg-[#F9F7FF] text-body border border-accentGray/50 rounded-2xl resize-none focus:border-secondary outline-none transition-all text-lg leading-relaxed"
 placeholder="Start typing your brilliance here..."
 />

 <div className="flex justify-between items-center mt-8">
 <div className="text-muted text-xs font-bold uppercase tracking-widest">
                    Last updated: {activeNote.updated_at ? new Date(activeNote.updated_at).toLocaleDateString() : (typeof activeNote.id === 'number' && activeNote.id > 1000000000 ? new Date(activeNote.id).toLocaleDateString() : "Recently")}
                  </div>
 <button
 onClick={() => deleteNote(activeNote.id)}
 className="px-4 py-2 text-red-400 font-bold flex items-center gap-2 hover:bg-red-500/10 rounded-xl transition-all"
 >
 <TrashIcon className="w-5 h-5" />
 Delete Note
 </button>
 </div>
 </div>
 )}
 </>
 )}
 </div>
 </div>
 );
};

export default MultiNotes;



